import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { X, Trash2, ShoppingCart } from 'lucide-react';

const SideCart = () => {
  const { cart, sideCartOpen, setSideCartOpen, removeFromCart, updateQuantity, getCartTotal, addToCart, allProducts, freeShippingThreshold } = useCart();
  const navigate = useNavigate();

  const suggestedProducts = useMemo(() => {
    if (!allProducts || allProducts.length === 0) return [];
    const cartIds = new Set(cart.map(i => i.id));
    const available = allProducts.filter(p => !cartIds.has(p.id) && p.in_stock !== false);
    const shuffled = [...available].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 4);
  }, [allProducts, cart]);

  const goToCheckout = () => {
    setSideCartOpen(false);
    navigate('/cart');
  };

  if (!sideCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setSideCartOpen(false)} />

      {/* Drawer from left */}
      <div className="fixed top-0 left-0 h-full w-[340px] max-w-[85vw] bg-white z-50 shadow-2xl flex flex-col animate-slide-in-left" dir="rtl" data-testid="side-cart-drawer">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100 bg-stone-50">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#D97706]" />
            <span className="font-bold text-stone-700" style={{ fontFamily: 'Heebo, sans-serif' }}>העגלה שלך</span>
          </div>
          <button onClick={() => setSideCartOpen(false)} className="p-1 hover:bg-stone-200 rounded-full transition-colors" data-testid="side-cart-close">
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto">
          {cart.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-stone-400 text-sm">העגלה ריקה</div>
          ) : (
            <div className="p-3 space-y-3">
              {cart.map(item => (
                <div key={item.id} className="flex gap-3 bg-stone-50 rounded-xl p-2.5" data-testid={`side-cart-item-${item.id}`}>
                  {item.image && <img src={item.image} alt={item.name} className="w-16 h-16 object-contain rounded-lg bg-white flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-stone-700 truncate">{item.name}</p>
                    {item.sideCartDiscount && <p className="text-[10px] font-bold text-green-600">מוצר ב10% הנחה</p>}
                    <div className="flex items-center gap-1">
                      {item.originalPrice && item.originalPrice !== item.price && (
                        <span className="text-xs text-stone-400 line-through">₪{item.originalPrice}</span>
                      )}
                      <p className="text-[#D97706] font-black text-sm">₪{item.price}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-6 h-6 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-sm font-bold transition-colors">-</button>
                      <span className="text-sm font-bold w-4 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-6 h-6 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-sm font-bold transition-colors">+</button>
                      <button onClick={() => removeFromCart(item.id)} className="mr-auto p-1 text-stone-300 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Suggested Products */}
          {suggestedProducts.length > 0 && (
            <div className="px-3 pb-3 mt-2">
              <p className="text-xs font-bold text-stone-400 mb-0.5">אולי יעניין אותך גם</p>
              <p className="text-xs font-black text-[#D97706] mb-2">10% הנחה על רכישת מוצר נוסף!</p>
              <div className="grid grid-cols-2 gap-2">
                {suggestedProducts.map(p => {
                  const discountedPrice = Math.round(p.price * 0.9);
                  return (
                    <div key={p.id} className="bg-stone-50 rounded-xl p-2 text-center" data-testid={`suggested-product-${p.id}`}>
                      {p.image && <img src={p.image} alt={p.name} className="w-12 h-12 object-contain mx-auto mb-1" />}
                      <p className="text-[10px] text-stone-600 font-bold truncate">{p.name?.split(' - ')[0]}</p>
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-[10px] text-stone-400 line-through">₪{p.price}</span>
                        <span className="text-[11px] text-[#D97706] font-black">₪{discountedPrice}</span>
                      </div>
                      <button
                        onClick={() => addToCart({ ...p, price: discountedPrice, originalPrice: p.price, sideCartDiscount: true })}
                        className="mt-1 w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-lg py-1 text-[10px] font-bold transition-colors"
                        data-testid={`add-suggested-${p.id}`}
                      >
                        הוסף לסל
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-stone-100 p-3 space-y-2 bg-white">
            {/* Free shipping indicator */}
            {freeShippingThreshold > 0 && (
              getCartTotal() >= freeShippingThreshold ? (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-center">
                  <span className="text-green-700 font-bold text-xs">משלוח חינם!</span>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-center">
                  <span className="text-amber-700 text-xs">חסרים לך <strong>₪{Math.ceil(freeShippingThreshold - getCartTotal())}</strong> למשלוח חינם</span>
                </div>
              )
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-stone-500">סה"כ</span>
              <span className="text-lg font-black text-stone-800">₪{getCartTotal().toLocaleString()}</span>
            </div>
            <button
              onClick={goToCheckout}
              className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-xl py-3 font-bold text-sm transition-colors"
              data-testid="side-cart-checkout"
            >
              מעבר לרכישה
            </button>
            <button
              onClick={() => setSideCartOpen(false)}
              className="w-full bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-xl py-2.5 font-bold text-sm transition-colors"
              data-testid="side-cart-continue"
            >
              המשך קנייה באתר
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-left {
          animation: slideInLeft 0.25s ease-out;
        }
      `}</style>
    </>
  );
};

export default SideCart;
